import {
    AlphaAttribute,
    ColorAttribute,
    getAttributesToSerialize,
    NumberAttribute,
    StringAttribute,
} from '../../../attributes';
import { Config } from '../../../../video-data/config';
import type { TextDynamicCircleRendererSettings } from '../renderer/TextDynamicCircleRenderer';
import { TextDynamicCircleRenderer } from '../renderer/TextDynamicCircleRenderer';
import type { Pixels } from '../../../../types';
import { int2rgba } from '../../../../libs/videoData';
import { GradientPoint } from '../../../GradientPoint';
import { TextAssetDynamicGraphic } from './TextAssetDynamicGraphic';

export class TextAssetDynamicCircle extends TextAssetDynamicGraphic {
    override _classType = 'CanvasTextDynamicCircle';

    override readonly type = Config.CIRCLE_GRAPHIC;
    @NumberAttribute({ min: 0, max: Infinity })
    accessor radius = 0;
    @ColorAttribute()
    accessor strokeColor = 0xffffff;
    @AlphaAttribute()
    accessor strokeAlpha = 1;
    @NumberAttribute({ min: 0, max: Infinity })
    accessor strokeWidth = 0;
    @ColorAttribute()
    accessor fill = 0xff00ff;
    @AlphaAttribute()
    accessor fillAlpha = 0.5;
    @StringAttribute({ choices: ['solid', 'linear'], animatable: false })
    accessor fillType: 'solid' | 'linear' = 'solid';

    gradientStartPoint = new GradientPoint({ parent: this, x: 0, y: 0.5, color: 0xff0000 });
    gradientEndPoint = new GradientPoint({ parent: this, x: 1, y: 0.5, color: 0x0000ff });

    override getCanvasRendererSettings(): TextDynamicCircleRendererSettings {
        const base = super.getCanvasRendererSettings();

        // make the stroke invisible for zero width
        const alpha = this.strokeWidth > 0 ? this.strokeAlpha : 0;
        const style = int2rgba(this.strokeColor, alpha);

        return {
            ...base,
            fill: int2rgba(this.fill, this.fillAlpha),
            type: this.type,
            radius: (this.radius * this.referenceToOutputMultiplier) as Pixels,
            strokeStyle: style,
            lineWidth: (this.strokeWidth * this.referenceToOutputMultiplier) as Pixels,
            fillType: this.fillType,
            gradientStartStop: int2rgba(
                this.gradientStartPoint.color,
                this.gradientStartPoint.alpha,
            ),
            gradientEndStop: int2rgba(this.gradientEndPoint.color, this.gradientEndPoint.alpha),
            gradientStartPoint: { x: this.gradientStartPoint.x, y: this.gradientStartPoint.y },
            gradientEndPoint: { x: this.gradientEndPoint.x, y: this.gradientEndPoint.y },
        };
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize
            .concat(getAttributesToSerialize(this))
            .concat(['gradientStartPoint', 'gradientEndPoint']);
    }

    /* The list of properties that can be animated during draw */
    override get animatableProperties() {
        // Since TextDynamicCircle is responsible for rendering our text we need to ask it which properties it
        // supports. See its rendering logic for more details.
        return TextDynamicCircleRenderer.animatableProperties;
    }

    override get animatableGeometricProperties() {
        return TextDynamicCircleRenderer.animatableGeometricProperties;
    }

    override get editableProperties() {
        return super.editableProperties.concat([
            'fill',
            'fillAlpha',
            'strokeColor',
            'strokeAlpha',
            'fillType',
            'gradientStartPoint',
            'gradientEndPoint',
        ]);
    }
}
